import React from 'react';
import {
  CUSTOM_CANVAS_APP_NATIVE_EVENTS,
  telemetrySendEvent,
} from '@packages/systems/designer/canvas';
import {dispatchInDesigner} from '../utils/dispatchInDesigner';
import {CUSTOM_CODE_ERROR_LOADING_SCRIPT_REPORTED_IN_CANVAS_APP} from '@packages/systems/designer/actions';

interface ScriptErrorEvent {
  src: string;
}

/**
 * Hook that reports unhandled promise rejections to Datadog
 */
const useReportUnhandledPromiseRejections = (): void => {
  React.useEffect(() => {
    const handleUnhandledPromiseRejection = (event: PromiseRejectionEvent) => {
      telemetrySendEvent({
        name: 'Unhandled promise rejection',
        data: {
          value: event.reason || 'Unknown error',
          stack: event.reason?.stack,
          type: 'unhandledrejection',
        },
      });
    };

    window.addEventListener(
      'unhandledrejection',
      handleUnhandledPromiseRejection
    );
    return () => {
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledPromiseRejection
      );
    };
  }, []);
};

/**
 * Hook that reports general JavaScript errors to Datadog
 */
const useReportJavaScriptErrors = (): void => {
  React.useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      telemetrySendEvent({
        name: 'JavaScript error',
        data: {
          value: event.message || 'Unknown error',
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          error: event.error?.stack || event.error,
          type: event.type,
        },
      });
    };

    window.addEventListener('error', handleError);

    return () => {
      window.removeEventListener('error', handleError);
    };
  }, []);
};

/**
 * Hook that reports resource loading errors (images, scripts, css, etc)
 */
const useReportResourceErrors = (): void => {
  React.useEffect(() => {
    const handleError = (event: Event) => {
      const target = event.target as HTMLElement;
      if (
        target instanceof HTMLImageElement ||
        target instanceof HTMLScriptElement ||
        target instanceof HTMLLinkElement
      ) {
        telemetrySendEvent({
          name: 'Resource load error',
          data: {
            value: target.tagName,
            src:
              (target as HTMLImageElement | HTMLScriptElement).src ||
              (target as HTMLLinkElement).href,
          },
        });
      }
    };

    // Using capture phase as resource errors typically need to be caught during the capture phase to be reliably detected across all browsers
    window.addEventListener('error', handleError, true);

    return () => {
      window.removeEventListener('error', handleError, true);
    };
  }, []);
};

/**
 * Hook that allows us to report script loading errors in custom code to Designer app and to DataDog
 */
const useReportScriptLoadingErrors = (): void => {
  // to avoid reporting the same error multiple times
  const sentErrorMessages = React.useRef<Set<string>>(new Set());

  React.useEffect(() => {
    const handleScriptError = (event: CustomEvent<ScriptErrorEvent>) => {
      const scriptSRC = event.detail?.src;

      if (!scriptSRC || sentErrorMessages.current.has(scriptSRC)) {
        return;
      }

      dispatchInDesigner({
        type: CUSTOM_CODE_ERROR_LOADING_SCRIPT_REPORTED_IN_CANVAS_APP,
        payload: {
          src: scriptSRC,
        },
      });

      telemetrySendEvent({
        name: 'Error loading script in custom code',
        data: {src: scriptSRC},
      });

      sentErrorMessages.current.add(scriptSRC);
    };

    window.addEventListener(
      CUSTOM_CANVAS_APP_NATIVE_EVENTS.ERROR_LOADING_SCRIPT_IN_CUSTOM_CODE,
      handleScriptError as EventListener
    );

    return () => {
      window.removeEventListener(
        CUSTOM_CANVAS_APP_NATIVE_EVENTS.ERROR_LOADING_SCRIPT_IN_CUSTOM_CODE,
        handleScriptError as EventListener
      );
    };
  }, []);
};

export const useReportCustomCodeErrors = () => {
  useReportScriptLoadingErrors();
  useReportJavaScriptErrors();
  useReportUnhandledPromiseRejections();
  useReportResourceErrors();
};
